import axios from 'axios';
import getQueryParam from './getQueryParam';
import { getCookieDomain } from './cookie';

const getEnvConfig = () => (window as any)._env_ || {};
const getTenantTag = (): string => getEnvConfig().TENANT || 'poland';

/**
 * Get the provider ID for a given broker
 */
const getProviderIdForBroker = (broker: string): string | null => {
  const envConfig = getEnvConfig();
  const brokerMap = envConfig.SSO_BROKER_PROVIDER_MAP;
  if (typeof brokerMap === 'object' && brokerMap !== null) {
    return brokerMap[broker] || null;
  }

  return null;
};
/**
 * Check if broker tag exist in auto-redirect list
 */
const isBrokerInAutoRedirectList = (broker: string): boolean => {
  const envConfig = getEnvConfig();
  const brokerList = envConfig.SSO_BROKERS_WITH_AUTO_REDIRECT;

  return brokerList.includes(broker);
};

/**
 * Check if current domain should show SSO login option
 * Uses the 'broker' GET parameter to determine if SSO should be available
 * Example: ?broker=superpolisa will show SSO if 'superpolisa' is in SSO_BROKER_PROVIDER_MAP
 */
export const shouldShowSSO = (): boolean => {
  // Get broker from URL parameter
  const brokerParam = getQueryParam('broker');

  if (!brokerParam) {
    return false;
  }

  // Check if the broker parameter exists in the broker-provider mapping
  const providerId = getProviderIdForBroker(brokerParam);
  const hasSSO = providerId !== null;

  return hasSSO;
};

/**
 * Check if should auto-redirect to SSO login based on broker parameter
 * Uses the 'broker' GET parameter and checks if it's in the auto-redirect list
 * Example: ?broker=superpolisa will auto-redirect if 'superpolisa' is in SSO_BROKERS_WITH_AUTO_REDIRECT
 */
export const shouldAutoRedirect = (): boolean => {
  // Get broker from URL parameter
  const brokerParam = getQueryParam('broker');

  if (!brokerParam) {
    return false;
  }

  // Check if the broker parameter exists in auto redirect list
  return isBrokerInAutoRedirectList(brokerParam);
};

/**
 * Get current page URL for redirect_uri
 */
const getCurrentRedirectUri = (providerId: string): string => `${window.location.origin}/callback/${providerId}`;

/**
 * Initiate SSO login by redirecting to the SSO provider
 */
export const initiateSSOLogin = (): void => {
  const envConfig = getEnvConfig();
  const BASE_API_URL = envConfig.API_IDENTIFIER_URL;

  // Get broker from URL parameter to determine provider
  const brokerParam = getQueryParam('broker');
  const PROVIDER_ID = brokerParam ? getProviderIdForBroker(brokerParam) : null;

  if (!PROVIDER_ID) {
    console.error('No provider found for broker:', brokerParam);
    return;
  }

  const redirectUri = encodeURIComponent(getCurrentRedirectUri(PROVIDER_ID));
  const ssoUrl = `${BASE_API_URL}/auth/sso/${getTenantTag()}/${PROVIDER_ID}?redirect_uri=${redirectUri}`;
  (window.top || window).location.href = ssoUrl;
};

/**
 * Check if current page load is an SSO callback
 */
export const isSSOCallback = (): boolean => !!(getQueryParam('code') && getQueryParam('state'));

/**
 * Handle SSO callback by extracting code and state from URL params
 */
export const handleSSOCallback = (providerId?: string): Promise<any> => {
  const envConfig = getEnvConfig();
  const BASE_API_URL = envConfig.API_IDENTIFIER_URL;

  if (!providerId) {
    throw new Error('Provider ID is required for SSO callback');
  }

  const code = getQueryParam('code');
  const state = getQueryParam('state');

  if (!code || !state) {
    throw new Error('Missing SSO callback parameters');
  }

  // Get all query parameters from current URL
  const urlParams = new URLSearchParams(window.location.search);
  const queryString = urlParams.toString();

  const callbackUrl = `${BASE_API_URL}/auth/sso/callback/${getTenantTag()}/${providerId}?${queryString}`;

  return fetch(callbackUrl, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    }
  }).then(response => {
    if (!response.ok) {
      throw new Error('SSO callback failed');
    }
    return response.json();
  });
};

/**
 * Exchange one-time trust auth code (issued by 3rd party system, e.g. OVB) for authentication result
 */
export const exchangeTrustAuthCode = (code: string) => {
  const envConfig = getEnvConfig();
  const BASE_API_URL = envConfig.API_IDENTIFIER_URL;
  const tenant = getQueryParam('tenant') || getTenantTag();

  // Tenant may come from query string, encode it so it can't change the request path
  return axios.post(`${BASE_API_URL}/auth/exchange/${encodeURIComponent(tenant)}`, { code }, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    }
  }).then(response => response.data);
};

/**
 * Allow redirects only to QMT app host or hosts under shared cookie domain
 */
export const isAllowedRedirectUrl = (url?: string | null): boolean => {
  if (!url) {
    return false;
  }

  try {
    const { protocol, hostname } = new URL(url);
    const cookieDomain = getCookieDomain();
    let qmtAppHost = '';

    try {
      qmtAppHost = new URL(getEnvConfig().QMT_APP_URL).hostname;
    } catch (e) {
      qmtAppHost = '';
    }

    if (protocol !== 'https:') {
      return false;
    }

    return hostname === qmtAppHost || hostname === cookieDomain || hostname.endsWith(`.${cookieDomain}`);
  } catch (e) {
    return false;
  }
};
